import type React from 'react';
import {Box, IconButton, Link, Typography} from '@mui/material';
import {ChevronRight, ExpandMore} from '@mui/icons-material';
import {type GridRenderCellParams, useGridApiContext} from '@mui/x-data-grid-premium';
import type {ExploreRow} from '../domain/exploreTree';

type Props = GridRenderCellParams<ExploreRow> & {onOpen: (row: ExploreRow) => void};

/**
 * The tree's Category column, indented by depth: the chevron expands or collapses a category with sub-categories, and
 * the name opens the category's profile (any level). Replaces the grid's default grouping cell.
 */
export const CategoryTreeCell: React.FC<Props> = ({id, row, rowNode, onOpen}) => {
    const apiRef = useGridApiContext();
    const isGroup = rowNode.type === 'group';
    const expanded = isGroup && rowNode.childrenExpanded;
    const children = row.children?.length ?? 0;
    const toggle = () => apiRef.current.setRowChildrenExpansion(id, !expanded);

    return (
        // One chevron's width per level, so a child's chevron sits under its parent's name and siblings with and
        // without sub-categories line up by name.
        <Box sx={{display: 'flex', alignItems: 'center', height: '100%', pl: `${rowNode.depth * 28}px`, minWidth: 0}}>
            <Box sx={{width: 28, flexShrink: 0}}>
                {isGroup && (
                    <IconButton size="small" aria-label={expanded ? `Collapse ${row.name}` : `Expand ${row.name}`}
                                onClick={e => {
                                    e.stopPropagation();
                                    toggle();
                                }}>
                        {expanded ? <ExpandMore fontSize="small"/> : <ChevronRight fontSize="small"/>}
                    </IconButton>
                )}
            </Box>
            <Link component="button" underline="hover" color="inherit" variant="body2" noWrap title={`Open ${row.name}`}
                  onClick={e => {
                      e.stopPropagation();
                      onOpen(row);
                  }}
                  sx={{fontWeight: 500, textAlign: 'left', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                {row.name}
            </Link>
            {children > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ml: 0.5, flexShrink: 0}}>{`(${children})`}</Typography>
            )}
        </Box>
    );
};
